/**
 * @fileoverview Help Center main page
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  HelpCircle, 
  Play, 
  Info, 
  Shield, 
  FileText, 
  Mail, 
  AlertTriangle,
  BookOpen,
  Users,
  Settings,
  Globe,
  GitBranch,
  Star
} from 'lucide-react';
import { HelpPageLayout } from '../../components/help/HelpPageLayout';

const HelpCenter: React.FC = () => {

  const helpCategories = [
    {
      icon: <Play className="w-8 h-8" />,
      title: "How to Play",
      description: "Learn how to use our spinner app and all its features",
      link: "/help/how-to-play",
      color: "bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300"
    },
    {
      icon: <Info className="w-8 h-8" />,
      title: "About Us",
      description: "Learn about our mission, team, and values",
      link: "/help/about",
      color: "bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-300"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Privacy Policy",
      description: "How we protect and handle your personal information",
      link: "/help/privacy-policy",
      color: "bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-300"
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Terms of Service",
      description: "Rules and guidelines for using our service",
      link: "/help/terms",
      color: "bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-300"
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Contact Us",
      description: "Get support, report bugs, or send us feedback",
      link: "/help/contact",
      color: "bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300"
    },
    {
      icon: <GitBranch className="w-8 h-8" />,
      title: "Version Info",
      description: "Current version details and update information",
      link: "/help/version",
      color: "bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300"
    },
    {
      icon: <HelpCircle className="w-8 h-8" />,
      title: "FAQ",
      description: "Find answers to frequently asked questions",
      link: "/help/faq",
      color: "bg-teal-100 dark:bg-teal-900 text-teal-600 dark:text-teal-300"
    },

    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Blogs & Tips",
      description: "Blogs, Tips, strategies, and creative & educational ways to use spinners",
      link: "https://spinnergames.fun/blogs",
      color: "bg-pink-100 dark:bg-pink-900 text-pink-600 dark:text-pink-300",
      isExternal: true
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Testimonials",
      description: "See what our users say about their experience",
      link: "https://spinnergames.fun/blogs/testimonials",
      color: "bg-yellow-100 dark:bg-yellow-900 text-yellow-600 dark:text-yellow-300",
      isExternal: true
    }
  ];

  return (
    <HelpPageLayout>
      <Helmet>
        <title>Help Center - Spinner Games !!</title>
        <meta name="description" content="Get help with Spinner Games !! Find guides, contact support, and learn how to use our interactive spinner app." />
                  <meta property="og:title" content="Help Center - Spinner Games !!" />
        <meta property="og:description" content="Comprehensive help and support for our spinner app." />
        <link rel="canonical" href="https://spinnergames.fun/help" />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Help Center",
          "description": "Help and support center for Spinner Games !!",
          "mainEntity": {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How do I use the spinner app?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Check our How to Play guide for step-by-step instructions."
                }
              },
              {
                "@type": "Question",
                "name": "How do I contact support?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Use our Contact Us page or email support@spinnergames.fun"
                }
              }
            ]
          }
        })}
        </script>
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-6">
            <HelpCircle className="w-10 h-10 text-blue-600 dark:text-blue-300" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Help Center
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8">
            Explore our help guide or connect with us — we're here to assist!
          </p>
          
          {/* Browser Translation Notice */}
          <div className="max-w-2xl mx-auto mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-start">
                <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                    Need help in your language?
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Use your browser's translation feature (Chrome, Firefox, Safari) to translate this help content into your preferred language.
                  </p>
                </div>
              </div>
              <a
                href="https://spinnergames.fun/blogs/translate-in-browser/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-full hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors ml-4 flex-shrink-0"
              >
                Blog
              </a>
            </div>
          </div>
        </div>

        {/* Help Categories */}
        <div className="mb-16">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            How Can We Help You?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {helpCategories.map((category, index) => {
              const CardWrapper = category.isExternal ? 'a' : Link;
              const linkProps = category.isExternal 
                ? { href: category.link, target: '_blank', rel: 'noopener noreferrer' }
                : { to: category.link };
              
              return (
                <CardWrapper
                  key={index}
                  {...linkProps}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 block"
                >
                  <div className={`w-16 h-16 ${category.color} rounded-full flex items-center justify-center mb-4 mx-auto`}>
                    {category.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2 text-center">
                    {category.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    {category.description}
                  </p>
                </CardWrapper>
              );
            })}
          </div>
        </div>

        {/* Back to App Button */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to App
          </Link>
        </div>
      </div>
    </HelpPageLayout>
  );
};

export default HelpCenter; 